/**
 * Admin Customer Hub.
 *
 * The operator-facing console for cross-customer actions. Lives at
 * `/dashboard/admin/customer-hub`. Backed by `/api/v2/admin/customer-hub/*`.
 *
 * Capabilities (single page):
 *   - Filter / search the full customer list with Stripe + privacy
 *     fields visible at a glance.
 *   - Multi-select (per row, "select all on page") -> bulk-action bar.
 *   - Per-row actions: change tier, toggle ZDR, change retention,
 *     force-enable EasyAI, copy config from another customer.
 *   - Bulk action: Send branded email (opens EmailBroadcastDialog).
 *
 * Coexistence: the original /admin/customers page is preserved as-is
 * for now (the user explicitly asked not to remove it). When this
 * page graduates to "production-ready", the old page can be archived.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Email as EmailIcon,
  ShieldOutlined as ShieldIcon,
  AllInclusive as AllInclusiveIcon,
  StickyNote2Outlined as NotesIcon,
  History as HistoryIcon,
  Upgrade as UpgradeIcon,
  ContentCopy as ContentCopyIcon,
  RocketLaunch as RocketLaunchIcon,
  Block as BlockIcon,
  VpnLock as ImpersonateIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAdminUser } from '../utils/auth';
import { getConfig } from '../services/config';
import { RelativeTime } from '../components/RelativeTime';
import EmailBroadcastDialog, {
  EmailRecipientsSpec,
} from '../components/EmailBroadcastDialog';

// ---------- Types ---------------------------------------------------

interface CustomerRow {
  id: string;
  customer_tag: string;
  name: string;
  primary_email: string | null;
  active: boolean;
  user_type: string;
  created_at: string;
  updated_at: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  subscription_tier: string | null;
  subscription_status: string | null;
  subscription_period_end: string | null;
  subscription_total_credits: number | null;
  subscription_credits_remaining: number | null;
  has_default_payment_method: boolean | null;
  zero_data_retention: boolean;
  zero_data_retention_set_at: string | null;
  zero_data_retention_set_by: string | null;
  credits_unlimited?: boolean;
  admin_notes?: string | null;
  admin_notes_updated_at?: string | null;
  admin_notes_updated_by?: string | null;
  last_active_at?: string | null;
  data_retention_days: number | null;
  data_retention_set_at: string | null;
  data_retention_set_by: string | null;
  is_external: boolean;
  is_easyai_packaged: boolean;
  has_paid_subscription: boolean;
  api_keys_count: number;
}

const TIER_OPTIONS = [
  'free',
  'starter',
  'builder',
  'pro',
  'scale',
  'easyai-1000',
  'external-packaged',
];
const STATUS_OPTIONS = ['active', 'trialing', 'past_due', 'unpaid', 'canceled', 'incomplete'];
const RETENTION_OPTIONS: Array<{ value: number | null; label: string }> = [
  { value: null, label: 'Forever' },
  { value: 0, label: 'Do not store' },
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '365 days' },
];

// ---------- Helpers -------------------------------------------------

/** Pick the right MUI Chip color for a Stripe subscription status. */
const statusColor = (
  status: string | null | undefined
): 'success' | 'info' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'success';
    case 'past_due':
      return 'warning';
    case 'unpaid':
    case 'incomplete':
    case 'incomplete_expired':
      return 'error';
    case 'canceled':
      return 'error';
    default:
      return 'default';
  }
};

const tierColor = (
  tier: string | null | undefined
): 'success' | 'info' | 'warning' | 'default' | 'primary' => {
  switch (tier) {
    case 'scale':
      return 'primary';
    case 'pro':
      return 'success';
    case 'builder':
      return 'info';
    case 'starter':
      return 'warning';
    default:
      return 'default';
  }
};

const formatRetention = (days: number | null): string => {
  if (days === null || days === undefined) return 'Forever';
  if (days === 0) return 'Do not store';
  if (days === 1) return '1 day';
  return `${days} days`;
};

// ---------- The page -----------------------------------------------

const AdminCustomerHub: React.FC = () => {
  const navigate = useNavigate();
  const { permissions, getIdToken, signInWithImpersonationToken } = useAuth();
  const apiBase = useMemo(() => getConfig().apiBaseUrl, []);

  // Auth gate (redirect non-admins on mount).
  useEffect(() => {
    if (permissions && !isAdminUser(permissions.userType)) {
      navigate('/dashboard');
    }
  }, [permissions, navigate]);

  // ---- List state ----
  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [zdrOnly, setZdrOnly] = useState(false);
  const [externalOnly, setExternalOnly] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ---- Selection state ----
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // ---- Per-row action menu ----
  const [actionAnchor, setActionAnchor] = useState<null | HTMLElement>(null);
  const [actionRow, setActionRow] = useState<CustomerRow | null>(null);

  // ---- Sub-dialogs ----
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState<EmailRecipientsSpec>({
    customer_ids: [],
  });
  const [emailRecipientsLabel, setEmailRecipientsLabel] = useState('');

  const [tierDialogOpen, setTierDialogOpen] = useState(false);
  const [tierDialogRow, setTierDialogRow] = useState<CustomerRow | null>(null);
  const [tierDialogTier, setTierDialogTier] = useState<string>('');
  const [tierDialogSkipStripe, setTierDialogSkipStripe] = useState(false);
  const [tierDialogReason, setTierDialogReason] = useState('');

  const [userTypeDialogOpen, setUserTypeDialogOpen] = useState(false);
  const [userTypeDialogRow, setUserTypeDialogRow] = useState<CustomerRow | null>(null);
  const [userTypeDialogValue, setUserTypeDialogValue] = useState<'admin' | 'customer'>('customer');
  const [userTypeDialogReason, setUserTypeDialogReason] = useState('');

  const [retentionDialogOpen, setRetentionDialogOpen] = useState(false);
  const [retentionDialogRow, setRetentionDialogRow] = useState<CustomerRow | null>(null);
  const [retentionDialogDays, setRetentionDialogDays] = useState<number | null>(null);
  const [retentionDialogReason, setRetentionDialogReason] = useState('');

  const [zdrDialogOpen, setZdrDialogOpen] = useState(false);
  const [zdrDialogRow, setZdrDialogRow] = useState<CustomerRow | null>(null);
  const [zdrDialogReason, setZdrDialogReason] = useState('');
  const [unlimitedDialogRow, setUnlimitedDialogRow] = useState<CustomerRow | null>(null);
  const [unlimitedDialogReason, setUnlimitedDialogReason] = useState('');
  const [notesDialogRow, setNotesDialogRow] = useState<CustomerRow | null>(null);
  const [notesDraft, setNotesDraft] = useState('');

  const [easyaiDialogOpen, setEasyaiDialogOpen] = useState(false);
  const [easyaiDialogRow, setEasyaiDialogRow] = useState<CustomerRow | null>(null);
  const [easyaiDialogReason, setEasyaiDialogReason] = useState('');

  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyDialogRow, setCopyDialogRow] = useState<CustomerRow | null>(null);
  const [copyDialogSourceId, setCopyDialogSourceId] = useState('');
  const [copyDialogKeys, setCopyDialogKeys] = useState('');
  const [copyDialogOverwrite, setCopyDialogOverwrite] = useState(false);
  const [copyDialogReason, setCopyDialogReason] = useState('');

  // Impersonation dialog state. Separated from `actionBusy` because
  // the sign-in flow has a longer "loading" window (mint token,
  // sign out admin, sign in custom token, redirect) and we don't
  // want it to gray out the table.
  const [impDialogOpen, setImpDialogOpen] = useState(false);
  const [impDialogRow, setImpDialogRow] = useState<CustomerRow | null>(null);
  const [impDialogReason, setImpDialogReason] = useState('');
  const [impDialogTtlMin, setImpDialogTtlMin] = useState<number>(30);
  const [impBusy, setImpBusy] = useState(false);
  const [impError, setImpError] = useState<string | null>(null);

  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [snackMsg, setSnackMsg] = useState<string | null>(null);

  // ---- Fetch list ----
  const fetchList = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getIdToken();
      const params = new URLSearchParams({
        page: String(page + 1),
        page_size: String(pageSize),
      });
      if (search.trim()) params.append('search', search.trim());
      if (tierFilter) params.append('tier', tierFilter);
      if (statusFilter) params.append('sub_status', statusFilter);
      if (zdrOnly) params.append('zdr_only', 'true');
      if (externalOnly) params.append('external_only', 'true');
      if (includeInactive) params.append('include_inactive', 'true');
      const resp = await fetch(
        `${apiBase}/api/v2/admin/customer-hub/customers?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      const data = json?.data;
      setRows(data?.rows || []);
      setTotal(Number(data?.total || 0));
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(`Failed to load customers: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, tierFilter, statusFilter, zdrOnly, externalOnly, includeInactive]);

  // Manual search submit (Enter key).
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    fetchList();
  };

  // ---- Selection helpers ----
  const allOnPageSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  const someOnPageSelected = rows.some((r) => selectedIds.has(r.id));
  const toggleSelectPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) rows.forEach((r) => next.delete(r.id));
      else rows.forEach((r) => next.add(r.id));
      return next;
    });
  };
  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ---- Action helpers ----
  // PATCH / POST helper used by every per-row action dialog.
  const callJson = async (path: string, method: 'PATCH' | 'POST' | 'PUT', body: any): Promise<any> => {
    setActionBusy(true);
    setActionError(null);
    try {
      const token = await getIdToken();
      const resp = await fetch(`${apiBase}/api/v2/admin/customer-hub${path}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const json = await resp.json();
      if (!resp.ok) {
        throw new Error(json?.detail || json?.message || `HTTP ${resp.status}`);
      }
      return json;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setActionError(msg);
      return null;
    } finally {
      setActionBusy(false);
    }
  };

  // ---- Per-row action menu ----
  const openActionMenu = (e: React.MouseEvent<HTMLElement>, row: CustomerRow) => {
    setActionAnchor(e.currentTarget);
    setActionRow(row);
  };
  const closeActionMenu = () => {
    setActionAnchor(null);
    setActionRow(null);
  };

  // ---- Open dialogs ----
  const openTierDialog = (row: CustomerRow) => {
    setTierDialogRow(row);
    setTierDialogTier(row.subscription_tier || '');
    setTierDialogSkipStripe(false);
    setTierDialogReason('');
    setTierDialogOpen(true);
    closeActionMenu();
  };
  const openUserTypeDialog = (row: CustomerRow) => {
    const current = row.user_type === 'admin' ? 'admin' : 'customer';
    setUserTypeDialogRow(row);
    setUserTypeDialogValue(current === 'admin' ? 'customer' : 'admin');
    setUserTypeDialogReason('');
    setUserTypeDialogOpen(true);
    closeActionMenu();
  };
  const openRetentionDialog = (row: CustomerRow) => {
    setRetentionDialogRow(row);
    setRetentionDialogDays(row.data_retention_days ?? null);
    setRetentionDialogReason('');
    setRetentionDialogOpen(true);
    closeActionMenu();
  };
  const openZdrDialog = (row: CustomerRow) => {
    setZdrDialogRow(row);
    setZdrDialogReason('');
    setZdrDialogOpen(true);
    closeActionMenu();
  };
  const openEasyaiDialog = (row: CustomerRow) => {
    setEasyaiDialogRow(row);
    setEasyaiDialogReason('');
    setEasyaiDialogOpen(true);
    closeActionMenu();
  };
  const openCopyDialog = (row: CustomerRow) => {
    setCopyDialogRow(row);
    setCopyDialogSourceId('');
    setCopyDialogKeys('');
    setCopyDialogOverwrite(false);
    setCopyDialogReason('');
    setCopyDialogOpen(true);
    closeActionMenu();
  };
  const openEmailForRow = (row: CustomerRow) => {
    setEmailRecipients({ customer_ids: [row.id] });
    setEmailRecipientsLabel(`${row.customer_tag} (${row.primary_email || 'no email'})`);
    setEmailDialogOpen(true);
    closeActionMenu();
  };
  const openImpersonateDialog = (row: CustomerRow) => {
    setImpDialogRow(row);
    setImpDialogReason('');
    setImpDialogTtlMin(30);
    setImpError(null);
    setImpDialogOpen(true);
    closeActionMenu();
  };

  // ----- Submit impersonation -----
  // Three-phase flow: mint token (audited backend call), swap
  // Firebase session, redirect. Errors at any stage abort cleanly
  // without partial state — if the swap fails the operator's
  // original admin session is already gone, but the dialog stays
  // open so they can read the error and re-login.
  const submitImpersonation = async () => {
    if (!impDialogRow) return;
    if (!impDialogReason.trim() || impDialogReason.trim().length < 4) {
      setImpError('Please provide a reason (min 4 chars).');
      return;
    }
    setImpBusy(true);
    setImpError(null);
    try {
      const result = await callJson(
        `/customers/${impDialogRow.id}/impersonate`,
        'POST',
        {
          reason: impDialogReason.trim(),
          ttl_seconds: Math.max(60, Math.min(60 * 60, impDialogTtlMin * 60)),
        }
      );
      if (!result?.data?.custom_token) {
        // callJson sets actionError on failure; surface it here too.
        setImpError(actionError || 'Failed to mint impersonation token.');
        return;
      }
      // Swap Firebase session. AuthContext takes care of clearing
      // the admin permissions and re-reading the impersonation
      // claims from the new ID token; the banner will appear as
      // soon as the auth state settles.
      await signInWithImpersonationToken(result.data.custom_token);
      // Land on the dashboard so the operator immediately sees the
      // customer's view rather than the (now-redirected) hub.
      navigate('/dashboard');
    } catch (e) {
      setImpError(e instanceof Error ? e.message : String(e));
    } finally {
      setImpBusy(false);
    }
  };

  // ---- Submit handlers ----
  const submitTier = async () => {
    if (!tierDialogRow || !tierDialogTier || !tierDialogReason.trim()) return;
    const result = await callJson(
      `/customers/${tierDialogRow.id}/tier`,
      'PATCH',
      {
        tier_id: tierDialogTier,
        skip_stripe: tierDialogSkipStripe,
        reason: tierDialogReason.trim(),
      }
    );
    if (result) {
      setSnackMsg(`Tier updated for ${tierDialogRow.customer_tag}`);
      setTierDialogOpen(false);
      fetchList();
    }
  };
  const submitUserType = async () => {
    if (!userTypeDialogRow || !userTypeDialogReason.trim()) return;
    const result = await callJson(
      `/customers/${userTypeDialogRow.id}/user-type`,
      'PATCH',
      {
        user_type: userTypeDialogValue,
        reason: userTypeDialogReason.trim(),
      }
    );
    if (result) {
      setSnackMsg(
        `${userTypeDialogRow.customer_tag} ${
          userTypeDialogValue === 'admin' ? 'is now a platform admin' : 'is no longer a platform admin'
        }`
      );
      setUserTypeDialogOpen(false);
      fetchList();
    }
  };
  const submitRetention = async () => {
    if (!retentionDialogRow || !retentionDialogReason.trim()) return;
    const result = await callJson(
      `/customers/${retentionDialogRow.id}/data-retention`,
      'PATCH',
      { days: retentionDialogDays, reason: retentionDialogReason.trim() }
    );
    if (result) {
      setSnackMsg(`Retention updated for ${retentionDialogRow.customer_tag}`);
      setRetentionDialogOpen(false);
      fetchList();
    }
  };
  const submitZdr = async () => {
    if (!zdrDialogRow || !zdrDialogReason.trim()) return;
    const result = await callJson(
      `/customers/${zdrDialogRow.id}/zdr`,
      'PATCH',
      { enabled: !zdrDialogRow.zero_data_retention, reason: zdrDialogReason.trim() }
    );
    if (result) {
      setSnackMsg(`ZDR ${zdrDialogRow.zero_data_retention ? 'disabled' : 'enabled'} for ${zdrDialogRow.customer_tag}`);
      setZdrDialogOpen(false);
      fetchList();
    }
  };
  const submitUnlimited = async () => {
    if (!unlimitedDialogRow || !unlimitedDialogReason.trim()) return;
    const enable = !unlimitedDialogRow.credits_unlimited;
    const result = await callJson(
      `/customers/${unlimitedDialogRow.id}/unlimited-credits`,
      'PATCH',
      { enabled: enable, reason: unlimitedDialogReason.trim() }
    );
    if (result) {
      setSnackMsg(`Unlimited credits ${enable ? 'on' : 'off'} for ${unlimitedDialogRow.customer_tag}`);
      setUnlimitedDialogRow(null);
      fetchList();
    }
  };
  const submitNotes = async () => {
    if (!notesDialogRow) return;
    const result = await callJson(`/customers/${notesDialogRow.id}/notes`, 'PUT', { notes: notesDraft });
    if (result) {
      setSnackMsg(`Notes saved for ${notesDialogRow.customer_tag}`);
      setNotesDialogRow(null);
      fetchList();
    }
  };
  const submitEasyai = async () => {
    if (!easyaiDialogRow || !easyaiDialogReason.trim()) return;
    const result = await callJson(
      `/customers/${easyaiDialogRow.id}/force-enable-easyai`,
      'POST',
      { reason: easyaiDialogReason.trim() }
    );
    if (result) {
      setSnackMsg(`EasyAI enabled for ${easyaiDialogRow.customer_tag}`);
      setEasyaiDialogOpen(false);
      fetchList();
    }
  };
  const submitCopy = async () => {
    if (!copyDialogRow || !copyDialogSourceId.trim() || !copyDialogReason.trim()) return;
    const keys = copyDialogKeys
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);
    if (keys.length === 0) {
      setActionError('Provide at least one key to copy.');
      return;
    }
    const result = await callJson(
      `/customers/${copyDialogRow.id}/copy-config-from`,
      'POST',
      {
        source_customer_id: copyDialogSourceId.trim(),
        keys,
        overwrite: copyDialogOverwrite,
        reason: copyDialogReason.trim(),
      }
    );
    if (result) {
      setSnackMsg(`Config copied to ${copyDialogRow.customer_tag}`);
      setCopyDialogOpen(false);
      fetchList();
    }
  };

  // ---- Bulk-email entry from action bar ----
  const openBulkEmail = (mode: 'selected' | 'all_paid' | 'by_current_filter') => {
    if (mode === 'selected') {
      setEmailRecipients({ customer_ids: Array.from(selectedIds) });
      setEmailRecipientsLabel(`${selectedIds.size} selected customer(s)`);
    } else if (mode === 'all_paid') {
      setEmailRecipients({ mode: 'all_paid' });
      setEmailRecipientsLabel('All paid customers (active + trialing)');
    } else {
      // 'by_current_filter' — use whatever the table is currently filtered to.
      const spec: EmailRecipientsSpec = {};
      if (tierFilter) {
        spec.mode = 'by_tier';
        spec.tiers = [tierFilter];
      } else if (statusFilter) {
        spec.mode = 'by_status';
        spec.statuses = [statusFilter];
      } else if (zdrOnly) {
        spec.mode = 'zdr_only';
      } else {
        spec.mode = 'all';
      }
      spec.include_inactive = includeInactive;
      setEmailRecipients(spec);
      setEmailRecipientsLabel(
        `By current filter` +
          (tierFilter ? ` — tier=${tierFilter}` : '') +
          (statusFilter ? ` — status=${statusFilter}` : '') +
          (zdrOnly ? ` — ZDR only` : '')
      );
    }
    setEmailDialogOpen(true);
  };

  // ---- Render ------------------------------------------------------

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Customer Hub
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Operator console — plans, unlimited credits, ZDR, retention, EasyAI, internal notes, and broadcast email. Row
            actions are under ⋮.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchList}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<EmailIcon />}
            onClick={() => openBulkEmail('all_paid')}
          >
            Email all paid customers
          </Button>
        </Stack>
      </Stack>

      {/* ---- Filters bar ---- */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
          flexWrap="wrap"
        >
          <Box component="form" onSubmit={handleSearchSubmit} sx={{ flex: 1, minWidth: 240 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search id / tag / name / email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Tier</InputLabel>
            <Select label="Tier" value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {TIER_OPTIONS.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch checked={zdrOnly} onChange={(e) => setZdrOnly(e.target.checked)} />
            }
            label="ZDR only"
          />
          <FormControlLabel
            control={
              <Switch
                checked={externalOnly}
                onChange={(e) => setExternalOnly(e.target.checked)}
              />
            }
            label="External only"
          />
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
            }
            label="Include inactive"
          />
        </Stack>
      </Paper>

      {/* ---- Bulk action bar ---- */}
      {selectedIds.size > 0 && (
        <Paper
          sx={{
            p: 1.5,
            mb: 2,
            bgcolor: 'primary.light',
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography sx={{ flex: 1 }}>
            <strong>{selectedIds.size}</strong> selected
          </Typography>
          <Button
            color="inherit"
            startIcon={<EmailIcon />}
            onClick={() => openBulkEmail('selected')}
          >
            Email selected
          </Button>
          <Button color="inherit" onClick={() => openBulkEmail('by_current_filter')}>
            Email all matching filter
          </Button>
          <Button color="inherit" onClick={() => setSelectedIds(new Set())}>
            Clear
          </Button>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      {snackMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSnackMsg(null)}>
          {snackMsg}
        </Alert>
      )}

      {/* ---- Table ---- */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={!allOnPageSelected && someOnPageSelected}
                    checked={allOnPageSelected}
                    onChange={toggleSelectPage}
                  />
                </TableCell>
                <TableCell>Tag</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Tier</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Credits</TableCell>
                <TableCell>ZDR</TableCell>
                <TableCell>Retention</TableCell>
                <TableCell>Flags</TableCell>
                <TableCell>API keys</TableCell>
                <TableCell>
                  <Tooltip title="Latest sign-in or function run">
                    <span>Last active</span>
                  </Tooltip>
                </TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={14} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={14} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No customers match.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    selected={selectedIds.has(row.id)}
                    sx={{ opacity: row.active ? 1 : 0.55 }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.has(row.id)}
                        onChange={() => toggleRow(row.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {row.customer_tag}
                      </Typography>
                      {row.user_type === 'admin' && (
                        <Chip
                          label="Platform admin"
                          size="small"
                          color="secondary"
                          sx={{ mt: 0.5, height: 20, fontSize: '0.68rem' }}
                        />
                      )}
                      <Typography variant="caption" color="textSecondary">
                        {row.id}
                      </Typography>
                      {row.admin_notes && (
                        <Tooltip title={<Box sx={{ whiteSpace: 'pre-wrap' }}>{row.admin_notes}</Box>}>
                          <Chip
                            icon={<NotesIcon />}
                            label="Notes"
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setNotesDialogRow(row);
                              setNotesDraft(row.admin_notes || '');
                            }}
                            sx={{ mt: 0.5, height: 20, fontSize: '0.68rem', display: 'flex', width: 'fit-content' }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.primary_email || '—'}</TableCell>
                    <TableCell>
                      {row.subscription_tier ? (
                        <Chip
                          label={row.subscription_tier}
                          size="small"
                          color={tierColor(row.subscription_tier)}
                        />
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          none
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.subscription_status ? (
                        <Chip
                          label={row.subscription_status}
                          size="small"
                          color={statusColor(row.subscription_status)}
                          variant="outlined"
                        />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {row.credits_unlimited ? (
                        <Chip label="Unlimited" size="small" color="success" variant="outlined" />
                      ) : row.subscription_credits_remaining !== null &&
                      row.subscription_credits_remaining !== undefined
                        ? row.subscription_credits_remaining.toLocaleString()
                        : row.subscription_total_credits
                        ? row.subscription_total_credits.toLocaleString()
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {row.zero_data_retention ? (
                        <Chip icon={<ShieldIcon />} label="ON" size="small" color="success" />
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          off
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatRetention(row.data_retention_days)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {row.is_easyai_packaged && (
                          <Chip
                            label="EasyAI"
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        )}
                        {row.is_external && (
                          <Chip
                            label="External"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                        {row.user_type === 'admin' && (
                          <Chip
                            icon={<ShieldIcon />}
                            label="Admin"
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        )}
                        {!row.active && (
                          <Chip
                            icon={<BlockIcon />}
                            label="Inactive"
                            size="small"
                            color="default"
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{row.api_keys_count}</TableCell>
                    <TableCell>
                      {row.last_active_at ? (
                        <RelativeTime value={row.last_active_at} />
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          never
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={new Date(row.updated_at).toLocaleString()}>
                        <span>
                          {new Date(row.updated_at).toLocaleDateString()}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => openActionMenu(e, row)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </Paper>

      {/* ---- Per-row action menu ---- */}
      <Menu anchorEl={actionAnchor} open={!!actionAnchor} onClose={closeActionMenu}>
        <MenuItem onClick={() => actionRow && openTierDialog(actionRow)}>
          <UpgradeIcon fontSize="small" sx={{ mr: 1 }} />
          Change tier…
        </MenuItem>
        <MenuItem onClick={() => actionRow && openUserTypeDialog(actionRow)}>
          <ShieldIcon fontSize="small" sx={{ mr: 1 }} />
          {actionRow?.user_type === 'admin' ? 'Remove platform admin…' : 'Make platform admin…'}
        </MenuItem>
        <MenuItem onClick={() => actionRow && openZdrDialog(actionRow)}>
          <ShieldIcon fontSize="small" sx={{ mr: 1 }} />
          {actionRow?.zero_data_retention ? 'Disable ZDR…' : 'Enable ZDR…'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!actionRow) return;
            setUnlimitedDialogRow(actionRow);
            setUnlimitedDialogReason('');
            closeActionMenu();
          }}
        >
          <AllInclusiveIcon fontSize="small" sx={{ mr: 1 }} />
          {actionRow?.credits_unlimited ? 'Restore credit limit…' : 'Make credits unlimited…'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!actionRow) return;
            setNotesDialogRow(actionRow);
            setNotesDraft(actionRow.admin_notes || '');
            closeActionMenu();
          }}
        >
          <NotesIcon fontSize="small" sx={{ mr: 1 }} />
          {actionRow?.admin_notes ? 'Edit notes…' : 'Add notes…'}
        </MenuItem>
        <MenuItem onClick={() => actionRow && openRetentionDialog(actionRow)}>
          <HistoryIcon fontSize="small" sx={{ mr: 1 }} />
          Set data retention…
        </MenuItem>
        <MenuItem
          onClick={() => actionRow && openEasyaiDialog(actionRow)}
          disabled={!!actionRow?.stripe_subscription_id}
        >
          <RocketLaunchIcon fontSize="small" sx={{ mr: 1 }} />
          Force-enable EasyAI…
        </MenuItem>
        <MenuItem onClick={() => actionRow && openCopyDialog(actionRow)}>
          <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
          Copy config from…
        </MenuItem>
        <MenuItem onClick={() => actionRow && openEmailForRow(actionRow)}>
          <EmailIcon fontSize="small" sx={{ mr: 1 }} />
          Send email…
        </MenuItem>
        <MenuItem
          onClick={() => actionRow && openImpersonateDialog(actionRow)}
          disabled={actionRow?.user_type === 'admin'}
          // No FirebaseUser to impersonate as → can't ship a token.
          // We don't have firebase_user count on the row; the
          // server-side endpoint will refuse with a clear error
          // message if the customer has no user. The button stays
          // enabled so the operator gets that explicit feedback
          // rather than a silent disabled state.
        >
          <ImpersonateIcon fontSize="small" sx={{ mr: 1 }} />
          Sign in as user…
        </MenuItem>
      </Menu>

      {/* ---- Tier dialog ---- */}
      <Dialog open={tierDialogOpen} onClose={() => setTierDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Change tier for {tierDialogRow?.customer_tag}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>New tier</InputLabel>
              <Select
                label="New tier"
                value={tierDialogTier}
                onChange={(e) => setTierDialogTier(e.target.value)}
              >
                {TIER_OPTIONS.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={tierDialogSkipStripe}
                  onChange={(e) => setTierDialogSkipStripe(e.target.checked)}
                />
              }
              label="Skip Stripe (DB-only — for comped/demo accounts)"
            />
            <TextField
              label="Reason (audit log)"
              value={tierDialogReason}
              onChange={(e) => setTierDialogReason(e.target.value)}
              required
              fullWidth
              size="small"
              placeholder="e.g. Comped Pro tier per CEO approval 2026-05-24"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTierDialogOpen(false)} disabled={actionBusy}>
            Cancel
          </Button>
          <Button
            onClick={submitTier}
            variant="contained"
            disabled={actionBusy || !tierDialogTier || !tierDialogReason.trim()}
          >
            {actionBusy ? 'Saving…' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Platform admin user_type dialog ---- */}
      <Dialog open={userTypeDialogOpen} onClose={() => setUserTypeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {userTypeDialogValue === 'admin' ? 'Make platform admin' : 'Remove platform admin'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity={userTypeDialogValue === 'admin' ? 'warning' : 'info'}>
              This changes <strong>{userTypeDialogRow?.customer_tag}</strong> from{' '}
              <code>{userTypeDialogRow?.user_type}</code> to <code>{userTypeDialogValue}</code>.
              {userTypeDialogValue === 'admin'
                ? ' Active users on this customer tenant will gain access to platform-admin tools after refresh.'
                : ' Active users on this customer tenant will lose access to platform-admin tools after refresh.'}
            </Alert>
            <TextField
              label="Reason (required)"
              multiline
              minRows={3}
              value={userTypeDialogReason}
              onChange={(e) => setUserTypeDialogReason(e.target.value)}
              fullWidth
              placeholder="Why is this tenant being granted or removed from platform-admin access?"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserTypeDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={userTypeDialogValue === 'admin' ? 'warning' : 'primary'}
            disabled={actionBusy || !userTypeDialogReason.trim()}
            onClick={submitUserType}
          >
            {userTypeDialogValue === 'admin' ? 'Make admin' : 'Remove admin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Retention dialog ---- */}
      <Dialog
        open={retentionDialogOpen}
        onClose={() => setRetentionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Set retention for {retentionDialogRow?.customer_tag}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Retention</InputLabel>
              <Select
                label="Retention"
                value={
                  retentionDialogDays === null ? 'null' : String(retentionDialogDays)
                }
                onChange={(e) =>
                  setRetentionDialogDays(
                    e.target.value === 'null' ? null : parseInt(e.target.value, 10)
                  )
                }
              >
                {RETENTION_OPTIONS.map((opt) => (
                  <MenuItem
                    key={String(opt.value)}
                    value={opt.value === null ? 'null' : String(opt.value)}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Reason (audit log)"
              value={retentionDialogReason}
              onChange={(e) => setRetentionDialogReason(e.target.value)}
              required
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetentionDialogOpen(false)} disabled={actionBusy}>
            Cancel
          </Button>
          <Button
            onClick={submitRetention}
            variant="contained"
            disabled={actionBusy || !retentionDialogReason.trim()}
          >
            {actionBusy ? 'Saving…' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- ZDR dialog ---- */}
      <Dialog open={zdrDialogOpen} onClose={() => setZdrDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {zdrDialogRow?.zero_data_retention ? 'Disable' : 'Enable'} ZDR for{' '}
          {zdrDialogRow?.customer_tag}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              {zdrDialogRow?.zero_data_retention
                ? 'Disabling ZDR allows AI calls to route to non-ZDR-eligible providers and the global Vertex region.'
                : 'Enabling ZDR restricts every AI call this account makes to providers covered by our no-train contract (Vertex AI, and OpenAI GPT unless the data region is US).'}
            </Alert>
            <TextField
              label="Reason (audit log)"
              value={zdrDialogReason}
              onChange={(e) => setZdrDialogReason(e.target.value)}
              required
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setZdrDialogOpen(false)} disabled={actionBusy}>
            Cancel
          </Button>
          <Button
            onClick={submitZdr}
            variant="contained"
            color={zdrDialogRow?.zero_data_retention ? 'warning' : 'success'}
            disabled={actionBusy || !zdrDialogReason.trim()}
          >
            {actionBusy ? 'Saving…' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Admin notes dialog ---- */}
      <Dialog open={!!notesDialogRow} onClose={() => setNotesDialogRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Notes for {notesDialogRow?.customer_tag}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Internal notes for platform admins: who the contact is, deals, special arrangements, why a setting was changed.
              Customers never see these.
            </Typography>
            <TextField
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              multiline
              minRows={5}
              fullWidth
              inputProps={{ maxLength: 5000 }}
              helperText={`${notesDraft.length}/5000`}
            />
            {notesDialogRow?.admin_notes_updated_at && (
              <Typography variant="caption" color="textSecondary">
                Last edited <RelativeTime value={notesDialogRow.admin_notes_updated_at} variant="caption" /> by{' '}
                {notesDialogRow.admin_notes_updated_by || 'unknown'}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesDialogRow(null)} disabled={actionBusy}>
            Cancel
          </Button>
          <Button
            onClick={submitNotes}
            variant="contained"
            disabled={actionBusy || notesDraft.trim() === (notesDialogRow?.admin_notes || '').trim()}
          >
            {actionBusy ? 'Saving…' : 'Save notes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Unlimited credits dialog ---- */}
      <Dialog open={!!unlimitedDialogRow} onClose={() => setUnlimitedDialogRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {unlimitedDialogRow?.credits_unlimited ? 'Restore credit limit for' : 'Unlimited credits for'}{' '}
          {unlimitedDialogRow?.customer_tag}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity={unlimitedDialogRow?.credits_unlimited ? 'info' : 'warning'}>
              {unlimitedDialogRow?.credits_unlimited
                ? 'Credit limits and auto-refill apply again from the next request, based on their plan.'
                : 'This account will never be blocked for credits and will never be auto-refilled or charged for overage. Usage is still recorded. Use for internal and test accounts.'}
            </Alert>
            <TextField
              label="Reason (audit log)"
              value={unlimitedDialogReason}
              onChange={(e) => setUnlimitedDialogReason(e.target.value)}
              required
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlimitedDialogRow(null)} disabled={actionBusy}>
            Cancel
          </Button>
          <Button
            onClick={submitUnlimited}
            variant="contained"
            color={unlimitedDialogRow?.credits_unlimited ? 'warning' : 'success'}
            disabled={actionBusy || unlimitedDialogReason.trim().length < 3}
          >
            {actionBusy ? 'Saving…' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Force-enable EasyAI dialog ---- */}
      <Dialog
        open={easyaiDialogOpen}
        onClose={() => setEasyaiDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Force-enable EasyAI for {easyaiDialogRow?.customer_tag}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Provisions the bundled EasyAI subscription (`easyai-1000`) — same path the
              external provisioning endpoint uses. Customer must already have a Stripe
              customer ID and no existing subscription.
            </Alert>
            <TextField
              label="Reason (audit log)"
              value={easyaiDialogReason}
              onChange={(e) => setEasyaiDialogReason(e.target.value)}
              required
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEasyaiDialogOpen(false)} disabled={actionBusy}>
            Cancel
          </Button>
          <Button
            onClick={submitEasyai}
            variant="contained"
            color="primary"
            startIcon={<RocketLaunchIcon />}
            disabled={actionBusy || !easyaiDialogReason.trim()}
          >
            {actionBusy ? 'Provisioning…' : 'Force-enable'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Copy config dialog ---- */}
      <Dialog
        open={copyDialogOpen}
        onClose={() => setCopyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Copy config to {copyDialogRow?.customer_tag}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Source customer ID or tag"
              value={copyDialogSourceId}
              onChange={(e) => setCopyDialogSourceId(e.target.value)}
              required
              fullWidth
              size="small"
              helperText="The customer to copy `config.*` keys from."
            />
            <TextField
              label="Keys (comma-separated)"
              value={copyDialogKeys}
              onChange={(e) => setCopyDialogKeys(e.target.value)}
              required
              fullWidth
              size="small"
              helperText="Top-level keys in `config` to copy. e.g. branding, knowledge_defaults, feature_flags"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={copyDialogOverwrite}
                  onChange={(e) => setCopyDialogOverwrite(e.target.checked)}
                />
              }
              label="Overwrite existing values on the target"
            />
            <TextField
              label="Reason (audit log)"
              value={copyDialogReason}
              onChange={(e) => setCopyDialogReason(e.target.value)}
              required
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDialogOpen(false)} disabled={actionBusy}>
            Cancel
          </Button>
          <Button
            onClick={submitCopy}
            variant="contained"
            disabled={
              actionBusy ||
              !copyDialogSourceId.trim() ||
              !copyDialogKeys.trim() ||
              !copyDialogReason.trim()
            }
          >
            {actionBusy ? 'Copying…' : 'Copy'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Email dialog ---- */}
      <EmailBroadcastDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        initialRecipients={emailRecipients}
        initialRecipientsLabel={emailRecipientsLabel}
        availableTiers={TIER_OPTIONS}
        onSendComplete={(res) => {
          setSnackMsg(
            `Broadcast sent: ${res.sent} delivered, ${res.failed} failed (resolved ${res.recipients_resolved}).`
          );
          setEmailDialogOpen(false);
        }}
      />

      {/* -----------------------------------------------------------
          Impersonation dialog ("Sign in as user")

          Why a dedicated confirmation step rather than a one-click
          action: signing in as a customer is operationally heavy
          (admin's own session is signed out, audit log line is
          written, customer-facing surfaces will start treating
          actions as the customer's). The dialog forces the operator
          to write a reason, which:
            (1) appears in the [AI_AUDIT] line so a future
                "who/why" question is answerable from grep alone,
            (2) shows in the impersonation banner so the operator
                themselves remembers why they started a session.
       --------------------------------------------------------- */}
      <Dialog
        open={impDialogOpen}
        onClose={() => !impBusy && setImpDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ImpersonateIcon color="warning" />
          Sign in as {impDialogRow?.customer_tag || 'customer'}
        </DialogTitle>
        <DialogContent>
          <Alert
            severity="warning"
            icon={<WarningIcon />}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              You are about to sign in as this customer. While the
              session is active:
            </Typography>
            <Box component="ul" sx={{ pl: 2.5, mb: 0 }}>
              <li>
                Your admin session will be signed out — you'll need
                to log in again afterwards.
              </li>
              <li>
                Every action is logged with your operator email as
                the actor. Audit trail is preserved.
              </li>
              <li>
                Billing changes, payment-method edits, ZDR / data-
                retention edits, and admin tools are <strong>blocked</strong>
                {' '}while impersonating — use the Hub for those.
              </li>
              <li>
                The session expires automatically (max 1 hour).
              </li>
            </Box>
          </Alert>
          {impDialogRow && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Customer
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {impDialogRow.customer_tag}
                {impDialogRow.primary_email && ` · ${impDialogRow.primary_email}`}
              </Typography>
            </Box>
          )}
          <TextField
            label="Reason (logged to audit)"
            value={impDialogReason}
            onChange={(e) => setImpDialogReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            placeholder="e.g. Building chains for ACME per support ticket #1234"
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 200 }}
            helperText={`${impDialogReason.length}/200`}
          />
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Session length
            </Typography>
            <Stack direction="row" spacing={1}>
              {[15, 30, 45, 60].map((m) => (
                <Button
                  key={m}
                  size="small"
                  variant={impDialogTtlMin === m ? 'contained' : 'outlined'}
                  onClick={() => setImpDialogTtlMin(m)}
                >
                  {m} min
                </Button>
              ))}
            </Stack>
          </Box>
          {impError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {impError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImpDialogOpen(false)} disabled={impBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={submitImpersonation}
            disabled={
              impBusy ||
              !impDialogReason.trim() ||
              impDialogReason.trim().length < 4
            }
            startIcon={<ImpersonateIcon />}
          >
            {impBusy ? 'Signing in…' : 'Sign in as user'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCustomerHub;
